import { useMsal } from '@azure/msal-react';
import { Badge, Menu, MenuButton, MenuItem, MenuList, MenuPopover, MenuTrigger, Skeleton, SkeletonItem, Table, TableBody, TableCell, TableCellActions, TableCellLayout, TableHeader, TableHeaderCell, TableRow } from '@fluentui/react-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import { TokenHelper } from '../../TokenHelper';
import { DeleteIcon, useTabStyles } from '../Styles/Editor';
import { TeamsState } from '../../store/TeamsState';
import { Button } from '@fluentui/react-components';
import { ConsultantActionCreators } from "../../store/consultants";

interface Log {
    id: string,
    prod: boolean,
    date: string,
    level: string,
    message: string,
    exception: string,
    category: string,
}

function BadgeColor(level: string) {
    switch (level) {
        case "CRITICAL": return "severe";
        case "WARNING": return "warning";
        case "INFORMATION": return "brand";
        case "DEBUG": return "informative";
        case "TRACE": return "informative";
        case "ERROR": return "danger";
    }
    return "severe";
}

export const Logs = () => {
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { t } = useTranslation();
    const stylesTab = useTabStyles();
    const [logs, setLogs] = React.useState<Log[] | undefined>(undefined);
    const [filter, setFilter] = React.useState<string | null>(null);
    const [filteredLogs, setFilteredLogs] = React.useState<Log[] | undefined>(undefined);
    const uniqueCategories = React.useMemo(
        () => {
            if (logs) {
                return new Set(logs.map(item => item.category));
            }
            return new Set([]);
        },
        [logs]
    );

    React.useEffect(() => {
        if (logs) {
            let filtered = logs;
            if (filter && filter.length != 0) {
                filtered = filtered
                    .filter(t => (t.message && t.message.toLowerCase().includes(filter.toLowerCase())));
            }
            setFilteredLogs(filtered);
        }
    }, [logs, filter]);

    const LoadLogs = () => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    fetch(`api/Logs`, {
                        method: "get",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 403) {
                                //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                            }
                            return response.json() as Promise<Log[]>;
                        })
                        .then(data => {
                            setLogs(data);
                        });
                }
            });
    };
    React.useEffect(() => {
        LoadLogs();
    }, [instance, teamsState]);
    const OnDeleteClick = (id: string) => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    fetch(`api/Logs/` + id, {
                        method: "delete",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 204 && logs) {
                                setLogs(logs.filter(l => l.id !== id));
                            }
                        });
                }
            });
    };
    const OnDeleteCategoryClick = (cat: string) => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    fetch(`api/Logs/Categories/` + cat, {
                        method: "delete",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 204 && logs) {
                                setLogs(logs.filter(l => l.category !== cat));
                            }
                        });
                }
            });
    };
    const OnDeleteAllForTestEnvLogClick = () => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    fetch(`api/Logs/AllForTestEnv`, {
                        method: "delete",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 204 && logs) {
                                setLogs(logs.filter(l => l.prod));
                            }
                        });
                }
            });
    };
    const OnDeleteAllPeppolTestDocsClick = () => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    fetch(`api/Logs/AllPeppolTestDocs`, {
                        method: "delete",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 204) {
                                dispatch(ConsultantActionCreators.RequestConsultantPeppolReceivedInvoices(instance, teamsState));
                            }
                        });
                }
            });
    };

    const columns = [
        { columnKey: "Date", label: t('Date'), className: stylesTab.width15 },
        { columnKey: "Category", label: "Category", className: stylesTab.width17 },
        { columnKey: "Message", label: "Message", className: stylesTab.width15andCenter },
        { columnKey: "Exception", label: "Exception", className: stylesTab.width15andCenter },
    ];


    return <div className={stylesTab.rootSub}>
        <div className={stylesTab.MainBar}>
            {uniqueCategories.size > 0 &&
                <>
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <MenuButton>Delete all log of Category</MenuButton>
                        </MenuTrigger>

                        <MenuPopover>
                            <MenuList>
                                {[...uniqueCategories].map((c) =>
                                    <MenuItem key={"m" + c}
                                        onClick={() => OnDeleteCategoryClick(c)}
                                    >
                                        {c}
                                    </MenuItem>
                                )}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    {logs && logs.find(c => !c.prod) !== undefined &&
                        <Button
                            onClick={() => OnDeleteAllForTestEnvLogClick()}
                        >
                            Delete all log from the Test Environment
                        </Button>
                    }
                    <Button
                        onClick={() => OnDeleteAllPeppolTestDocsClick()}
                    >
                        Delete Peppol test documents
                    </Button>
                </>
            }
            <Button
                onClick={() => LoadLogs()}
            >
                Refresh
            </Button>
        </div>
        <Table arial-label="Default table">
            <TableHeader>
                <TableRow>
                    {columns.map((column) => (
                        <TableHeaderCell key={column.columnKey} className={column.className}>
                            {column.label}
                        </TableHeaderCell>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {filteredLogs === undefined
                    ? [...Array(10)].map((_, index) =>
                        <TableRow key={index}>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                        </TableRow>)
                    : filteredLogs.map(log =>
                        <TableRow key={log.id}>
                            <TableCell align="left">
                                <TableCellLayout className={stylesTab.flexrow}>
                                    <div className={stylesTab.flexrow}>
                                        {new Date(log.date).toLocaleString()}

                                        <Badge appearance="filled" color={BadgeColor(log.level)}>
                                            {log.level}
                                        </Badge>
                                        {
                                            !log.prod &&
                                            <Badge appearance="filled" color="brand">
                                                Debug Env
                                            </Badge>
                                        }
                                    </div>
                                </TableCellLayout>
                                <TableCellActions>
                                    <Button
                                        icon={<DeleteIcon />}
                                        aria-label="Delete"
                                        size="small"
                                        onClick={() => { OnDeleteClick(log.id); }}
                                    />
                                </TableCellActions>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout truncate>
                                    {log.category}
                                </TableCellLayout>
                            </TableCell>
                            <TableCell>{log.message}</TableCell>
                            <TableCell>{log.exception}</TableCell>
                        </TableRow>
                    )}
            </TableBody>
        </Table>

    </div>;
};
